import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Tier hierarchy (matches backend)
const TIER_LEVELS = {
  free: 0,
  basic: 1,
  individual: 1,
  pro: 2,
  enterprise: 3,
};

const TIER_DISPLAY = {
  free: 'Individual',
  basic: 'Individual',
  individual: 'Individual',
  pro: 'Teams',
  enterprise: 'Enterprise',
};

export const useTierAccess = () => {
  const { user, api } = useAuth();
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTierInfo = useCallback(async () => {
    try {
      const res = await api().get('/tier/info');
      setTierInfo(res.data);
    } catch {
      // Fallback to local calculation
      const tier = user?.tier || 'free';
      setTierInfo({
        tier,
        tier_display: TIER_DISPLAY[tier] || 'Individual',
        features: {
          search: true,
          basic_report: true,
          enhanced_report: hasTierAccess(tier, 'pro'),
          ai_insite_report: hasTierAccess(tier, 'enterprise'),
          scheduled_reports: hasTierAccess(tier, 'enterprise'),
          watchlist: hasTierAccess(tier, 'pro'),
          email_alerts: hasTierAccess(tier, 'pro'),
          ai_chat: hasTierAccess(tier, 'pro'),
          crm_access: hasTierAccess(tier, 'enterprise'),
          teams: hasTierAccess(tier, 'pro'),
          white_label: hasTierAccess(tier, 'enterprise'),
        },
        limits: {},
        datasets_access: user?.datasets_access || [],
      });
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    if (user) fetchTierInfo();
    else setLoading(false);
  }, [user, fetchTierInfo]);

  const tier = user?.tier || 'free';
  const isAdmin = user?.role === 'admin';

  const hasTierAccess = (userTier, requiredTier) => {
    if (isAdmin) return true;
    return (TIER_LEVELS[userTier] || 0) >= (TIER_LEVELS[requiredTier] || 0);
  };

  const canAccess = (feature) => {
    if (isAdmin) return true;
    return tierInfo?.features?.[feature] ?? false;
  };

  const tierDisplay = TIER_DISPLAY[tier] || 'Individual';

  return {
    tier,
    tierDisplay,
    tierInfo,
    loading,
    isAdmin,
    canAccess,
    hasTierAccess: (requiredTier) => hasTierAccess(tier, requiredTier),
    refreshTierInfo: fetchTierInfo,
  };
};

export { TIER_LEVELS, TIER_DISPLAY };
